import React, { useState, useEffect } from 'react';
import {
    Box, Drawer, List, ListItem, ListItemIcon, ListItemText, Menu,
    MenuItem, Divider, Typography, IconButton, Snackbar, Alert, useTheme, useMediaQuery, Button
} from '@mui/material';
import { DashboardOutlinedIcon, ContactsOutlinedIcon, SurroundSoundOutlinedIcon, BarChartOutlinedIcon, ExtensionOutlinedIcon, SettingsOutlinedIcon, ArrowForwardIcon, MoreVertIcon, RocketLaunchOutlinedIcon, GroupsOutlinedIcon } from 'icons/materialIcons';
import {
    FeedbackOutlinedIcon,
    LogoutOutlinedIcon,
    PersonOutlineOutlinedIcon,
    PowerOutlinedIcon,
    HelpOutlineOutlinedIcon,
    LocalOfferOutlinedIcon
} from 'icons/materialIcons';
import avatarPlaceholder from 'images/avatar-cropped.png';
import { useNavigate } from 'react-router-dom';
import { getUserById } from 'apis/authApi';
import RequestAFeatureModal from 'components/support/RequestAFeatureModal';
import { useAuth } from 'contexts/AuthContext';
import NotificationSnackbar from 'components/common/notification/NotificationSnackbar';
import { getDefaultCardByUserId } from 'apis/cardApi';

const TeamV2Sidenav = ({ onMenuClick }) => {
    const [anchorEl, setAnchorEl] = React.useState(null);
    const [menuAnchorEl, setMenuAnchorEl] = React.useState(null);
    const navigate = useNavigate();
    const [userData, setUserData] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const { logout, authState } = useAuth();
    const [profilePicture, setProfilePicture] = useState(avatarPlaceholder);
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('lg'));
    const [isExpanded, setIsExpanded] = useState(!isSmallScreen);
    const userName = authState?.user?.fullName || 'User'; // Use fullName from auth state
    const userId = authState?.user?.userId; // Use userId from auth state



    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success'
    });

    // Effect to handle screen size changes
    useEffect(() => {
        setIsExpanded(!isSmallScreen);
    }, [isSmallScreen]);

    useEffect(() => {
        const fetchDefaultCardProfile = async () => {
            try {
                if (userId) {
                    // ✅ ENHANCED: Retry logic with exponential backoff for race condition handling
                    let lastError = null;
                    const maxRetries = 3;
                    const baseDelay = 1000; // 1 second base delay

                    for (let attempt = 0; attempt <= maxRetries; attempt++) {
                        try {
                            console.log(`🔍 Sidenav: Attempt ${attempt + 1}/${maxRetries + 1} to fetch default card profile`);

                            const defaultCard = await getDefaultCardByUserId(userId);
                            if (defaultCard && defaultCard.profilePictureUrl) {
                                setProfilePicture(defaultCard.profilePictureUrl);
                                console.log('🔍 Sidenav: Default card profile picture set successfully');
                                return; // Success, exit retry loop
                            } else {
                                console.log('🔍 Sidenav: Default card found but no profile picture URL');
                                return; // No profile picture, but card exists, so don't retry
                            }
                        } catch (error) {
                            lastError = error;
                            console.log(`🔍 Sidenav: Attempt ${attempt + 1} failed:`, error.message);

                            // If this is the last attempt, throw the error
                            if (attempt === maxRetries) {
                                break;
                            }

                            // Calculate delay with exponential backoff
                            const delay = baseDelay * Math.pow(2, attempt);
                            console.log(`🔍 Sidenav: Waiting ${delay}ms before retry...`);
                            await new Promise(resolve => setTimeout(resolve, delay));
                        }
                    }

                    // If we get here, all retries failed
                    console.error('🔍 Sidenav: All retry attempts failed for fetching default card profile:', lastError);
                }
            } catch (error) {
                console.error('Error fetching default card profile:', error);
            }
        };

        fetchDefaultCardProfile();
    }, [userId]);

    const handleSidebarExpand = () => {
        if (isSmallScreen && !isExpanded) {
            setIsExpanded(true);
        }
    };

    const handleMenuItemClick = (menu) => {
        if (isSmallScreen && !isExpanded) {
            setIsExpanded(true);
        }
        onMenuClick(menu);
    };

    const handleSnackbarClose = () => {
        setSnackbar(prev => ({ ...prev, open: false }));
    };

    const handleSubmitSuccess = () => {
        setSnackbar({
            open: true,
            message: 'Feature request sent successfully!',
            severity: 'success'
        });
    };

    const handleSubmitError = (errorMessage) => {
        setSnackbar({
            open: true,
            message: errorMessage,
            severity: 'error'
        });
    };

    const handleOpenModal = () => {
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
    };

    const handleLogout = async () => {
        try {
            await logout();
        } catch (error) {
            console.error('Logout failed:', error);
        }
    };

    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleAppClick = () => {
    };

    const handleMenuClick = (event) => {
        setMenuAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setMenuAnchorEl(null);
    };

    const handleSubscribe = () => {
        handleClose();
        navigate(`/dashboard/subscribe`);
    };

    useEffect(() => {
        fetchUserData();
    }, [authState?.user?.userId]);


    const fetchUserData = async () => {
        try {
            const idToken = localStorage.getItem('authToken');
            if (!idToken) {
                throw new Error('No auth token found');
            }
            const response = await getUserById(userId, idToken);
            setUserData(response.data);
        } catch (error) {
            console.error('Error fetching user data:', error.message);
        }
    };
    return (
        <>
            <Drawer
                sx={{
                    width: isSmallScreen ? (isExpanded ? '15rem' : '3.5rem') : '15rem',
                    flexShrink: 0,
                    '& .MuiDrawer-paper': {
                        width: isSmallScreen ? (isExpanded ? '15rem' : '3.5rem') : '15rem',
                        boxSizing: 'border-box',
                        transition: theme.transitions.create('width', {
                            easing: theme.transitions.easing.sharp,
                            duration: theme.transitions.duration.enteringScreen,
                        }),
                        overflowX: 'hidden',
                        overflowY: 'hidden', // Hide vertical scrollbar
                        '&::-webkit-scrollbar': {
                            display: 'none', // Hide scrollbar for WebKit browsers
                        },
                        scrollbarWidth: 'none', // Hide scrollbar for Firefox
                        msOverflowStyle: 'none', // Hide scrollbar for IE/Edge
                    },
                }}
                variant="permanent"
                anchor="left"
            >
                <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', height: '100%' }}>
                    {/* Logo Section - Always visible */}
                    <Box sx={{ paddingTop: '1.875rem', paddingBottom: '0.625rem', display: 'flex', alignItems: 'center', marginLeft: '1.25rem', justifyContent: 'start' }}>
                        <Box sx={{ display: 'flex', alignItems: 'baseline' }}>
                            <Typography
                                variant="h6"
                                sx={{
                                    fontWeight: 600,
                                    color: '#333',
                                    fontSize: isExpanded ? '1.1rem' : '0.9rem',
                                    letterSpacing: '0.03125rem',
                                    textTransform: 'none',
                                    textAlign: 'center'
                                }}>
                                Touch
                            </Typography>
                            <Typography
                                variant="body2"
                                sx={{
                                    fontWeight: 600,
                                    color: '#4f4f4f',
                                    fontSize: isExpanded ? '1.1rem' : '0.9rem',
                                    letterSpacing: '0.03125rem',
                                    textTransform: 'none',
                                    marginLeft: '0.25rem'
                                }}>
                                Teams
                            </Typography>
                        </Box>
                    </Box>

                    {/* Main Content Section */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                        {/* Navigation Menu */}
                        <List>
                            {[
                                { text: 'Members', icon: <PersonOutlineOutlinedIcon sx={{ fontSize: '1.25rem' }} />, onClick: () => handleMenuItemClick('members') },
                                { text: 'Subteams', icon: <GroupsOutlinedIcon sx={{ fontSize: '1.25rem' }} />, onClick: () => handleMenuItemClick('subteams') },
                                { text: 'Templates', icon: <LocalOfferOutlinedIcon sx={{ fontSize: '1.25rem' }} />, onClick: () => handleMenuItemClick('templates') },
                                { text: 'Leads', icon: <ContactsOutlinedIcon sx={{ fontSize: '1.25rem' }} />, onClick: () => handleMenuItemClick('leads') },
                                { text: 'Devices', icon: <SurroundSoundOutlinedIcon sx={{ fontSize: '1.25rem' }} />, onClick: () => handleMenuItemClick('devices') },
                                { text: 'Insights', icon: <BarChartOutlinedIcon sx={{ fontSize: '1.25rem' }} />, onClick: () => handleMenuItemClick('insights') },
                                { text: 'Integrations', icon: <ExtensionOutlinedIcon sx={{ fontSize: '1.25rem' }} />, onClick: () => handleMenuItemClick('integrations') },
                                { text: 'Notifications', icon: <HelpOutlineOutlinedIcon sx={{ fontSize: '1.25rem' }} />, onClick: () => handleMenuItemClick('notifications') },
                                { text: 'Settings', icon: <SettingsOutlinedIcon sx={{ fontSize: '1.25rem' }} />, onClick: () => handleMenuItemClick('settings') }
                            ].map((item) => (
                                <ListItem
                                    key={item.text}
                                    onClick={item.onClick}
                                    sx={{
                                        justifyContent: (!isSmallScreen || isExpanded) ? 'flex-start' : 'center',
                                        px: (!isSmallScreen || isExpanded) ? 2 : 1,
                                        cursor: 'pointer',
                                        '&:hover': {
                                            backgroundColor: 'rgba(0, 0, 0, 0.04)'
                                        }
                                    }}
                                >
                                    <ListItemIcon
                                        sx={{
                                            minWidth: (!isSmallScreen || isExpanded) ? 40 : 'auto',
                                            justifyContent: 'center'
                                        }}
                                        onClick={(e) => {
                                            if (!isExpanded) {
                                                e.stopPropagation();
                                                handleSidebarExpand();
                                            }
                                        }}
                                    >
                                        {item.icon}
                                    </ListItemIcon>
                                    {(!isSmallScreen || isExpanded) && (
                                        <ListItemText
                                            primary={item.text}
                                            primaryTypographyProps={{
                                                sx: {
                                                    fontSize: '0.8rem',
                                                    fontWeight: 600,
                                                    color: "#4f4f4f",
                                                    marginLeft: 0
                                                }
                                            }}
                                        />
                                    )}
                                </ListItem>
                            ))}
                        </List>

                        {/* User Avatar in collapsed view */}
                        {isSmallScreen && !isExpanded && (
                            <Box sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                mt: 2,
                                mb: 2
                            }}>
                                <Box
                                    sx={{
                                        cursor: 'pointer'
                                    }}
                                    onClick={handleSidebarExpand}
                                >
                                    <img
                                        src={profilePicture}
                                        alt="User Avatar"
                                        style={{
                                            width: '2.5rem',
                                            height: '2.5rem',
                                            borderRadius: '50%',
                                            objectFit: 'cover'
                                        }}
                                        onError={(e) => {
                                            e.target.onerror = null;
                                            e.target.src = avatarPlaceholder;
                                        }}
                                    />
                                </Box>
                            </Box>
                        )}

                        {/* Spacer */}
                        <Box sx={{ flexGrow: 1 }} />

                        {/* Expanded view content */}
                        {(!isSmallScreen || isExpanded) && (
                            <>
                                {/* Members Status Box */}
                                <Box sx={{
                                    margin: 2,
                                    marginBottom: 1,
                                    padding: 2,
                                    backgroundColor: '#f8f9fa',
                                    borderRadius: '0.5rem',
                                    border: '0.0625rem solid #e9ecef'
                                }}>
                                    <Typography variant="body2" sx={{
                                        fontSize: '0.8rem',
                                        fontWeight: 600,
                                        color: '#4f4f4f',
                                        marginBottom: 3
                                    }}>
                                        Members logged in
                                    </Typography>

                                    {/* Progress Bar Container with Percentage and Member Count */}
                                    {(() => {
                                        const loggedInMembers = 200;
                                        const totalMembers = 343;
                                        const percentage = (loggedInMembers / totalMembers) * 100;

                                        return (
                                            <Box sx={{
                                                position: 'relative',
                                                marginBottom: 2
                                            }}>
                                                {/* Progress Bar */}
                                                <Box sx={{
                                                    width: '100%',
                                                    height: 8,
                                                    backgroundColor: '#e0e0e0',
                                                    border: '0.0625rem solid #c5c5c5',
                                                    borderRadius: 5,
                                                    overflow: 'hidden'
                                                }}>
                                                    <Box sx={{
                                                        width: `${percentage}%`,
                                                        height: '100%',
                                                        backgroundColor: '#29aef8',
                                                        border: '0.0625rem solid #2499da',
                                                        borderRadius: 5,
                                                        transition: 'width 0.3s ease-in-out'
                                                    }} />
                                                </Box>

                                                {/* Percentage - Top Right */}
                                                <Typography variant="body2" sx={{
                                                    position: 'absolute',
                                                    top: -20,
                                                    right: 0,
                                                    fontSize: '0.8rem',
                                                    fontWeight: 600,
                                                    color: '#29aef8'
                                                }}>
                                                    {Math.round(percentage)}%
                                                </Typography>

                                                {/* Member Count - Bottom Left */}
                                                <Typography variant="body2" sx={{
                                                    position: 'absolute',
                                                    bottom: -20,
                                                    left: 0,
                                                    fontSize: '0.7rem',
                                                    color: '#b0b0b0'
                                                }}>
                                                    {loggedInMembers}/{totalMembers} members logged in
                                                </Typography>
                                            </Box>
                                        );
                                    })()}

                                    {/* Send Invite Button */}
                                    <Button
                                        variant="outlined"
                                        fullWidth
                                        sx={{
                                            borderColor: '#dee2e6',
                                            color: '#4f4f4f',
                                            backgroundColor: 'white',
                                            fontWeight: 600,
                                            borderRadius: '0.375rem',
                                            textTransform: 'none',
                                            fontSize: '0.8rem',
                                            padding: '0.5rem 1rem',
                                            marginTop: 2, // Added more margin above
                                            '&:hover': {
                                                backgroundColor: '#f8f9fa',
                                                borderColor: '#adb5bd'
                                            }
                                        }}
                                    >
                                        Send Invite
                                    </Button>
                                </Box>

                                {/* Share Card Feature Section */}
                                <Box sx={{ padding: '0.5rem', backgroundColor: '#E3F2FD', borderRadius: '0.5rem', margin: '1rem', marginBottom: '0.5rem' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                        <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#4f4f4f' }}>
                                            Refer a team to Touch
                                        </Typography>
                                        <RocketLaunchOutlinedIcon sx={{
                                            fontSize: isSmallScreen ? '1.125rem' : '1.25rem',
                                            color: '#4f4f4f'
                                        }} />
                                    </Box>
                                </Box>

                                {/* User Profile Section in expanded view */}
                                <Box sx={{
                                    p: 2,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <img
                                            src={profilePicture}
                                            alt="User Avatar"
                                            style={{
                                                width: '2.5rem',
                                                height: '2.5rem',
                                                borderRadius: '50%',
                                                objectFit: 'cover'
                                            }}
                                            onError={(e) => {
                                                e.target.onerror = null;
                                                e.target.src = avatarPlaceholder;
                                            }}
                                        />
                                        <Box sx={{ marginLeft: '1rem' }}>
                                            <Typography variant="body2" sx={{ fontSize: "0.8rem", fontWeight: "bold" }}>
                                                {userName || userData?.fullName}
                                            </Typography>
                                            <Typography variant="body1" sx={{ color: 'text.secondary', fontSize: "0.7rem", fontWeight: 600 }}>
                                                USER
                                            </Typography>
                                        </Box>
                                    </Box>
                                    <IconButton onClick={handleMenuClick}>
                                        <MoreVertIcon />
                                    </IconButton>
                                </Box>


                                {/*
                                <Box
                                    onClick={handleSubscribe}
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                        width: '100%',
                                        cursor: 'pointer',
                                        alignItems: 'center',
                                        '&:hover': {
                                            backgroundColor: '#E3F2FD',
                                            borderRadius: '0.25rem',
                                            transition: 'background-color 0.3s'
                                        },
                                        backgroundColor: 'transparent',
                                        padding: '0.875rem 1rem',
                                        borderRadius: '0.25rem',
                                        transition: 'background-color 0.3s'
                                    }}
                                >
                                    <Typography variant="body2" sx={{ color: '#0277BD', fontWeight: 600, fontSize: '0.75rem' }}>
                                        Upgrade Now
                                    </Typography>
                                </Box>
                                */}

                            </>
                        )}
                    </Box>
                </Box>

                {/* Menu */}
                <Menu
                    anchorEl={menuAnchorEl}
                    open={Boolean(menuAnchorEl)}
                    onClose={handleMenuClose}
                    anchorOrigin={{
                        vertical: 'top',
                        horizontal: 'left',
                    }}
                    transformOrigin={{
                        vertical: 'bottom',
                        horizontal: 'right',
                    }}
                    PaperProps={{
                        sx: {
                            padding: '0.5rem',
                            borderRadius: '0.5rem',
                            boxShadow: '0 0.25rem 0.5rem rgba(0, 0, 0, 0.2)',
                        }
                    }}
                >
                    <MenuItem onClick={() => { handleMenuClose(); handleOpenModal(); }}>
                        <FeedbackOutlinedIcon sx={{ fontSize: '1.25rem', color: '#757575', marginRight: '0.5rem' }} />
                        Request a Feature
                    </MenuItem>
                    <MenuItem onClick={() => { handleMenuClose(); handleLogout(); }}>
                        <LogoutOutlinedIcon sx={{ fontSize: '1.25rem', color: '#757575', marginRight: '0.5rem' }} />
                        Logout
                    </MenuItem>
                </Menu>
            </Drawer>

            {/* Modals and Notifications */}
            <RequestAFeatureModal
                open={isModalOpen}
                onClose={handleCloseModal}
                onSubmitSuccess={handleSubmitSuccess}
                onSubmitError={handleSubmitError}
            />

            <NotificationSnackbar
                open={snackbar.open}
                message={snackbar.message}
                severity={snackbar.severity}
                handleClose={handleSnackbarClose}
            />
        </>
    );
};

export default TeamV2Sidenav;
